import { Button, Card, CardContent, Grid, Step, StepLabel, Stepper } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Dispatch, SetStateAction, useCallback, useEffect, useRef, useState } from "react";
import NotAssignedTable from "../../Pages/UserManagementDetailPage/NotAssignedTable";
import { useCreateActivateUserMutation } from "../../Queries/UserManagementQueries";
import { getClients } from "../../Services/API/BaseClientManagement";
import { getUserRoles } from "../../Services/API/UserManagement";
import { resendInvite, trimAccountFields } from "../../Services/API/UserAccountActions";
import { BaseClient } from "../../Types/BaseClientManagement";
import { AccountOutcome, ActivateUser, CreateActivateUser, UserClientRole } from "../../Types/Users";
import LoadingBar from "../LoadingBar/LoadingBar";
import { StyledTextField } from "../TextField/TextField";

const useStyles = makeStyles((theme) => ({
  root: {
    [theme.breakpoints.down("sm")]: {
      width: "100%",
      height: "100vh",
      display: "block",
      overflow: "scroll",
    },
    justifyContent: "space-between",
    width: "50vw",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  textField: {
    "& .Mui-error": {
      color: "rgba(255,0,0,.7)",
    },
    "& .MuiFormHelperText-root": {
      color: "rgba(255,0,0,.7)",
    },
    "& .MuiInput-underline.Mui-error:after": {
      borderBottomColor: "rgba(255,0,0,.7) !important",
    },
  },
  table: {
    [theme.breakpoints.down("sm")]: {
      paddingLeft: "0px",
      marginTop: "0px",
      height: "500px",
    },
    width: "100%",
    paddingLeft: "40px",
    marginBottom: "-50px",
    position: "relative",
  },
  error: {
    color: theme.palette.error.main,
  },
  inviteStatus: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "10px",
  },
  buttonContainer: {
    [theme.breakpoints.down("sm")]: {
      position: "absolute",
      left: "3px",
    },
  },
}));

interface CreateUserModalProps {
  openModal: Dispatch<SetStateAction<boolean>>;
  setCreateProcessing: Dispatch<SetStateAction<boolean>>;
}

const steps = ["User Information", "Assign To Client(s)"];

export default function CreateUserModal({ openModal, setCreateProcessing }: CreateUserModalProps) {
  const classes = useStyles();

  const { mutate: createNewUser, isLoading: createIsLoading } = useCreateActivateUserMutation();

  const [userFirstName, setUserFirstName] = useState("");
  const [userLastName, setUserLastName] = useState("");
  const [userEmail, setUserEmail] = useState("");
  const [userPhone, setUserPhone] = useState("");
  const [showInputError, setShowInputError] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [unassignedBaseClients, setUnassignedBaseClients] = useState<BaseClient[]>([]);
  const [createdUser, setCreatedUser] = useState<ActivateUser | null>(null);
  const [invite, setInvite] = useState<AccountOutcome | null>(null);
  const [createError, setCreateError] = useState("");
  const [resendPending, setResendPending] = useState(false);
  // isLoading lands a render later, so a second click in the same tick would post create again.
  const createPendingRef = useRef(false);

  const checkAllEntered = (fields: CreateActivateUser) => {
    if (
      fields.EmailAddress === "" ||
      fields.FirstName === "" ||
      fields.LastName === "" ||
      (fields.PhoneNumber.length > 0 && fields.PhoneNumber.length < 7)
    ) {
      return false;
    }
    return true;
  };

  const compare = (a: string, b: string) => {
    if (a < b) {
      return -1;
    }
    if (a > b) {
      return 1;
    }
    return 0;
  };

  const zipData = (baseClients: BaseClient[], userClientRoles: UserClientRole[]) => {
    userClientRoles.forEach((ucr) => {
      baseClients.forEach((baseClient) => {
        if (baseClient.id === ucr.baseClientId && (ucr.roleId !== null || ucr.crmRole !== 0)) {
          baseClients.splice(baseClients.indexOf(baseClient), 1);
        }
      });
    });
    setUnassignedBaseClients(baseClients);
  };

  const populate = (userId: string) => {
    getClients()
      .then((baseClients) => {
        baseClients.forEach((baseClient) => {
          if (!baseClient.hasCareers && !baseClient.hasCrm) {
            baseClients.splice(baseClients.indexOf(baseClient), 1);
          }
        });
        baseClients.sort((a, b) => {
          const order = compare(a.clientName, b.clientName);
          return order;
        });
        return getUserRoles(userId).then((roles) => {
          zipData(baseClients, roles);
        });
      })
      .catch((err) => {
        console.error("Failed to load base clients:", err);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleNext = useCallback(
    function createUser() {
      if (activeStep === 0) {
        if (createPendingRef.current) return;
        const userData = trimAccountFields({
          FirstName: userFirstName,
          LastName: userLastName,
          EmailAddress: userEmail,
          PhoneNumber: userPhone,
        });
        setUserFirstName(userData.FirstName);
        setUserLastName(userData.LastName);
        setUserEmail(userData.EmailAddress);

        if (checkAllEntered(userData)) {
          setShowInputError(false);
          setCreateError("");

          createPendingRef.current = true;
          createNewUser(userData, {
            onSettled: () => {
              createPendingRef.current = false;
            },
            onSuccess: (outcome) => {
              if (!outcome.created) {
                setCreateError(outcome.message);
                return;
              }
              setCreatedUser(outcome.user);
              setInvite(outcome.invite);
              populate(outcome.user.id);
              setActiveStep((prevActiveStep) => prevActiveStep + 1);
            },
          });
        } else {
          setShowInputError(true);
        }
      } else {
        openModal(false);
      }
    },
    [
      createNewUser,
      activeStep,
      checkAllEntered,
      populate,
      setActiveStep,
      userEmail,
      userFirstName,
      userLastName,
      userPhone,
    ],
  );

  const handleFinish = () => {
    openModal(false);
  };

  const handleResend = (userId: string) => {
    setResendPending(true);
    resendInvite(userId).then((outcome) => {
      setInvite(outcome);
      setResendPending(false);
    });
  };

  useEffect(() => {
    setCreateProcessing(createIsLoading);
  }, [createIsLoading]);

  return (
    <div className={classes.root}>
      <LoadingBar loading={createIsLoading} />
      <Stepper activeStep={activeStep}>
        {steps.map((label) => {
          const stepProps = {};
          const labelProps = {};

          return (
            <Step key={label} {...stepProps}>
              <StepLabel {...labelProps}>{label}</StepLabel>
            </Step>
          );
        })}
      </Stepper>
      <Card>
        <CardContent>
          {activeStep === 0 ? (
            <Grid>
              <Grid container spacing={1}>
                <Grid xs={12} sm={6} item>
                  <StyledTextField
                    InputLabelProps={{ style: { color: "rgba(0,0,0,.6)" } }}
                    className={classes.textField}
                    placeholder="User First Name"
                    label="User First Name"
                    variant="standard"
                    fullWidth
                    required
                    error={showInputError && userFirstName === ""}
                    helperText={showInputError && userFirstName === "" ? "First Name Required" : ""}
                    inputProps={{ "data-test": "create-user-first-name" }}
                    value={userFirstName}
                    onChange={(e) => setUserFirstName(e.target.value)}
                  />
                </Grid>
                <Grid xs={12} sm={6} item>
                  <StyledTextField
                    InputLabelProps={{ style: { color: "rgba(0,0,0,.6)" } }}
                    className={classes.textField}
                    placeholder="User Last Name"
                    label="User Last Name"
                    variant="standard"
                    fullWidth
                    required
                    error={showInputError && userLastName === ""}
                    helperText={showInputError && userLastName === "" ? "Last Name Required" : ""}
                    inputProps={{ "data-test": "create-user-last-name" }}
                    value={userLastName}
                    onChange={(e) => setUserLastName(e.target.value)}
                  />
                </Grid>
                <Grid xs={12} sm={6} item>
                  <StyledTextField
                    InputLabelProps={{ style: { color: "rgba(0,0,0,.6)" } }}
                    className={classes.textField}
                    placeholder="User Email"
                    label="User Email"
                    variant="standard"
                    fullWidth
                    required
                    error={showInputError && userEmail === ""}
                    helperText={showInputError && userEmail === "" ? "Email Required" : ""}
                    inputProps={{ "data-test": "create-user-email" }}
                    value={userEmail}
                    onChange={(e) => setUserEmail(e.target.value)}
                  />
                </Grid>
                <Grid xs={12} sm={6} item>
                  <StyledTextField
                    InputLabelProps={{ style: { color: "rgba(0,0,0,.6)" } }}
                    className={classes.textField}
                    placeholder="User Mobile Phone"
                    label="User Mobile Phone"
                    variant="standard"
                    fullWidth
                    error={showInputError && userPhone.length > 0 && userPhone.length < 7}
                    helperText={
                      showInputError && userPhone.length > 0 && userPhone.length < 7
                        ? "Invalid Phone Number"
                        : ""
                    }
                    inputProps={{ "data-test": "create-user-phone" }}
                    value={userPhone}
                    onChange={(e) => setUserPhone(e.target.value)}
                  />
                </Grid>
              </Grid>
              {createError ? (
                <p className={classes.error} data-test="create-user-error">
                  {createError}
                </p>
              ) : null}
            </Grid>
          ) : null}
          {activeStep === 1 && createdUser ? (
            <>
              <div className={classes.inviteStatus}>
                <span
                  className={invite?.ok ? undefined : classes.error}
                  data-test="create-user-invite-status"
                >
                  {invite?.message}
                </span>
                {invite && !invite.ok ? (
                  <Button
                    color="primary"
                    variant="outlined"
                    disabled={resendPending}
                    data-test="create-user-resend-invite"
                    onClick={() => handleResend(createdUser.id)}
                  >
                    Resend Invite
                  </Button>
                ) : null}
              </div>
              <div className={classes.table}>
                <NotAssignedTable
                  unassignedBaseClients={unassignedBaseClients}
                  populate={() => populate(createdUser.id)}
                  loading={loading}
                  user={{ id: createdUser.id, firstName: createdUser.firstName }}
                />
              </div>
            </>
          ) : null}
          <Grid className={classes.buttonContainer} container style={{ justifyContent: "flex-end" }}>
            {activeStep === 0 ? (
              <Grid xs={6} sm={3} item>
                <Button
                  disabled={createIsLoading}
                  style={{ marginTop: "30px", width: "95%" }}
                  color="primary"
                  type="submit"
                  variant="outlined"
                  onClick={() => openModal(false)}
                >
                  Cancel
                </Button>
              </Grid>
            ) : null}
            <Grid xs={6} sm={3} item>
              <Button
                disabled={createIsLoading}
                style={{ marginTop: "30px", width: "95%" }}
                type="submit"
                variant="contained"
                color="primary"
                onClick={() => (activeStep === 0 ? handleNext() : handleFinish())}
              >
                {activeStep === 0 ? "Next" : "Finish"}
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </div>
  );
}
